import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { ExportToCsv } from 'export-to-csv';

import { Appointments_Order_By } from '@gql/base-types';

import { buildAppointmentsCsvRows, ExportableAppointment, resolveAppointmentsCsvFilename } from '../export/appointmentsCsvExport';
import { AppointmentFilters, filterMobileServiceAppointments, toStatusFilter } from '../export/filterMobileServiceAppointments';
import { GetMobileServiceAppointmentsExportDocument } from '../queries/getMobileServiceAppointmentsExport.query.generated';

export interface AppointmentsExportInput {
  customerId: number;
  /** The tab's date window: the "all results" query's bounds and the filename. */
  windowStartIso: string;
  windowEndIso: string;
  /** The tab's sort, so the file reads in the order the table shows. */
  orderBy: Appointments_Order_By[];
  /** The tab's client-side filters, re-applied to the "all results" rows. */
  filters: AppointmentFilters;
}

/**
 * The Appointments tab's CSV export, in the shape of the Moves page export: the export dialog's
 * open state, "current page" written straight from the rows on screen, and "all results" from a
 * dedicated lazy no-cache query over the whole date window (the page query is capped, so an
 * all-results export over its data would silently stop at the cap).
 *
 * Both exports toast their own outcome. `exportAll` also rethrows after toasting, so the dialog
 * awaiting it can keep itself open for a retry instead of closing on a failed export.
 */
export function useAppointmentsExport({ customerId, windowStartIso, windowEndIso, orderBy, filters }: AppointmentsExportInput) {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [fetchExportRows] = useLazyQuery(GetMobileServiceAppointmentsExportDocument, { fetchPolicy: 'no-cache' });

  const openExportDialog = useCallback(() => setExportDialogOpen(true), []);
  const closeExportDialog = useCallback(() => setExportDialogOpen(false), []);

  const writeCsv = useCallback(
    (appointments: readonly ExportableAppointment[]) => {
      const rows = buildAppointmentsCsvRows(appointments);
      if (!rows.length) {
        toast.error('Failed to generate CSV - No appointments found!', { toastId: 'export-csv-error' });
        return;
      }
      new ExportToCsv({
        filename: resolveAppointmentsCsvFilename(windowStartIso, windowEndIso),
        useKeysAsHeaders: true,
      }).generateCsv(rows);
      toast.success('Generated CSV!', { autoClose: 2000, toastId: 'export-csv-success' });
    },
    [windowStartIso, windowEndIso]
  );

  /** The rows currently on screen, as they are. */
  const exportCurrentPage = useCallback(
    (appointments: readonly ExportableAppointment[]) => {
      try {
        writeCsv(appointments);
      } catch (error) {
        toast.error('Failed to generate CSV!', { toastId: 'export-csv-error' });
        console.error('Failed to generate CSV:', error);
      }
    },
    [writeCsv]
  );

  /** Every appointment in the window that passes the tab's filters. */
  const exportAll = useCallback(async () => {
    try {
      // The lazy query's promise resolves with `error` set rather than rejecting, so a failed
      // query would otherwise read as "no appointments found" and be reported as such.
      // The status chips go to the server so the download is only the statuses asked for; the
      // search (and the chips again, harmlessly) are applied to the rows by the tab's own filter.
      const { data, error } = await fetchExportRows({
        variables: { customerId, start: windowStartIso, end: windowEndIso, orderBy, statusFilter: toStatusFilter(filters.selectedStatuses) },
      });
      if (error) throw error;
      writeCsv(filterMobileServiceAppointments(data?.appointments ?? [], filters));
    } catch (error) {
      // Its own toast id: the success toast may still be on screen from a current-page export,
      // and react-toastify drops a new toast whose id is already showing.
      toast.error('Failed to generate CSV!', { toastId: 'export-csv-error' });
      console.error('Failed to generate CSV:', error);
      throw error;
    }
  }, [fetchExportRows, customerId, windowStartIso, windowEndIso, orderBy, filters, writeCsv]);

  return { exportDialogOpen, openExportDialog, closeExportDialog, exportCurrentPage, exportAll };
}
